import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { StepShell } from '../../components/RoomChrome'
import { ApiRequestError, bookAppointment, fetchAvailability } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import { sessionKey } from '../useSession'
import type { StepProps } from '../types'

/** Days on screen before "More days" — two weeks is what a bride plans in. */
const DAYS_SHOWN = 8

/**
 * Choose a day, choose a time, confirm.
 *
 * What is bookable is the server's answer, for the appointment type her line
 * books — the tier's hours, minus what the room already holds. Times are drawn
 * in the BOUTIQUE's timezone, because the appointment is in Los Angeles
 * wherever the phone is.
 *
 * Nothing she has told us is asked again. Her name and number were the first
 * screen; the email, the one thing this screen may add, is optional unless the
 * flow says otherwise.
 */
export function BookingStep(props: StepProps<'booking'>) {
  // Her appointment type books on Koalendar: no times here, only the way there.
  if (props.ctx.view.booksOnline && props.ctx.view.visit === undefined) return <OnlineBooking token={props.ctx.token} locale={props.ctx.locale} />
  return <SlotBooking {...props} />
}

/**
 * Booking that happens on Koalendar, reached from a screen that used to lead
 * to the times: the way on to `/reserve`, which takes the card and hands over
 * to Koalendar. `docs/23`. A button rather than a redirect: going back from
 * `/reserve` lands here, and a page that sent her on again by itself would be
 * a page she could never leave backwards.
 */
function OnlineBooking({ token, locale }: { readonly token: string; readonly locale: string }) {
  const t = uiStrings(locale)
  const href = `/reserve?s=${encodeURIComponent(token)}`
  return (
    <StepShell title={t.reserveTitle} body={t.bookOnlineNote}>
      <a className="fr-btn fr-btn-primary block w-full text-center" href={href}>
        {t.reserveToCalendar}
      </a>
    </StepShell>
  )
}

function SlotBooking({ step, ctx }: StepProps<'booking'>) {
  const { view, locale, token } = ctx
  const t = uiStrings(locale)
  const client = useQueryClient()
  const timeZone = view.tenant.timezone

  const availability = useQuery({
    queryKey: ['availability', token, view.session.lineKey ?? null] as const,
    queryFn: ({ signal }) => fetchAvailability(token, signal),
    staleTime: 30_000,
    enabled: view.visit === undefined,
  })

  const days = useMemo(() => availability.data?.days ?? [], [availability.data])
  const [dayCount, setDayCount] = useState(DAYS_SHOWN)
  const [day, setDay] = useState<string | null>(null)
  const [startsAt, setStartsAt] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [taken, setTaken] = useState(false)

  const selectedDay = day ?? days[0]?.date ?? null
  const slots = days.find((d) => d.date === selectedDay)?.slots ?? []

  const emailMode = step.email
  const emailValid = email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const emailReady = emailMode === 'required' ? email.trim() !== '' && emailValid : emailValid

  const line = view.flow.lines.find((l) => l.key === view.session.lineKey) ?? view.flow.lines[0]
  const room = view.flow.steps.find((s) => s.kind === 'fitting_room')

  const book = useMutation({
    mutationFn: () =>
      bookAppointment(token, { startsAt: startsAt ?? '', ...(emailMode !== 'hidden' && email.trim() ? { email: email.trim() } : {}) }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: sessionKey(token) })
      if (room) ctx.goTo(room.id)
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && error.code === 'slot_taken') {
        setTaken(true)
        setStartsAt(null)
        void availability.refetch()
      }
    },
  })

  const weekday = (date: string) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`))
  const monthDay = (date: string) =>
    new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`))
  const clock = (iso: string) => new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(iso))

  // Already booked: this screen has nothing left to do but say so.
  if (view.visit) {
    return (
      <StepShell
        title={t.yourAppointment}
        body={formatDateTime(view.visit.scheduledAt, locale, timeZone)}
        footer={
          room ? (
            <button type="button" className="fr-btn fr-btn-primary w-full" onClick={() => ctx.goTo(room.id)}>
              {t.continueBrowsing}
            </button>
          ) : undefined
        }
      >
        <></>
      </StepShell>
    )
  }

  return (
    <StepShell
      title={text(step.title, locale)}
      body={text(line?.bookingNote, locale) || text(step.body, locale)}
      footer={
        <div className="pb-2">
          {book.isError && !taken ? (
            <p role="alert" className="pb-2 text-center text-[0.8125rem]" style={{ color: 'var(--fr-secondary)' }}>
              {t.bookingFailed}
            </p>
          ) : null}
          <button
            type="button"
            className="fr-btn fr-btn-primary w-full"
            disabled={!startsAt || !emailReady || book.isPending}
            onClick={() => {
              setTaken(false)
              book.mutate()
            }}
          >
            {book.isPending ? t.confirming : text(step.confirmLabel, locale) || t.confirm}
          </button>
        </div>
      }
    >
      {availability.data ? (
        <p className="fr-eyebrow mb-5">
          {[availability.data.tierLabel, t.appointmentLength(availability.data.durationMinutes)].join(' · ')}
        </p>
      ) : null}

      {taken ? (
        <p role="alert" className="mb-4 text-[0.875rem] leading-relaxed" style={{ color: 'var(--fr-secondary)' }}>
          {t.timeTaken}
        </p>
      ) : null}

      {availability.isPending ? (
        <p className="fr-eyebrow animate-pulse">{t.loading}</p>
      ) : availability.isError || days.length === 0 ? (
        <p className="text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
          {t.noTimes}
        </p>
      ) : (
        <>
          <h2 className="fr-eyebrow mb-3">{t.chooseDay}</h2>
          <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label={t.chooseDay}>
            {days.slice(0, dayCount).map((d) => {
              const selected = d.date === selectedDay
              return (
                <button
                  key={d.date}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-selected={selected}
                  className="fr-tile"
                  style={{ minHeight: '4rem', padding: '0.6rem 0.2rem', gap: '0.2rem' }}
                  onClick={() => {
                    setDay(d.date)
                    setStartsAt(null)
                  }}
                >
                  <span className="fr-eyebrow" style={{ letterSpacing: '0.08em' }}>
                    {weekday(d.date)}
                  </span>
                  <span className="text-[0.875rem] leading-none tabular-nums">{monthDay(d.date)}</span>
                </button>
              )
            })}
          </div>
          {days.length > dayCount ? (
            <div className="mt-2 text-center">
              <button type="button" className="fr-btn-quiet fr-btn-target" onClick={() => setDayCount((n) => n + DAYS_SHOWN)}>
                {t.moreDays}
              </button>
            </div>
          ) : null}

          <h2 className="fr-eyebrow mt-7 mb-3">{t.chooseTime}</h2>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={t.chooseTime}>
            {slots.map((slot) => {
              const selected = slot.startsAt === startsAt
              return (
                <button
                  key={slot.startsAt}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-selected={selected}
                  className="fr-tile"
                  style={{ minHeight: '3rem', padding: '0.6rem 0.2rem' }}
                  onClick={() => setStartsAt(slot.startsAt)}
                >
                  <span className="text-[0.9375rem] leading-none tabular-nums">{clock(slot.startsAt)}</span>
                </button>
              )
            })}
          </div>

          {emailMode !== 'hidden' ? (
            <div className="mt-7 flex flex-col gap-1.5">
              <label htmlFor="fr-booking-email" className="fr-eyebrow">
                {emailMode === 'required' ? t.emailRequired : t.emailOptional}
              </label>
              <input
                id="fr-booking-email"
                className="fr-input"
                type="email"
                autoComplete="email"
                inputMode="email"
                maxLength={160}
                value={email}
                aria-invalid={!emailValid ? true : undefined}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-[0.75rem] leading-snug" style={{ color: emailValid ? 'var(--fr-ink-faint)' : 'var(--fr-secondary)' }}>
                {emailValid ? t.emailHelp : t.intakeProblem({ ok: false, reason: 'not_an_email' })}
              </p>
            </div>
          ) : null}
        </>
      )}
    </StepShell>
  )
}
